import { useState, useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import RelatedServices from '../../../components/feature/RelatedServices';
import RelatedIndustries from '../../../components/feature/RelatedIndustries';

export default function FunctionalIntegrativePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={scrolled} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 lg:px-12 overflow-hidden bg-gradient-to-br from-[#F5F3FF] via-white to-[#FAF5FF]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-[#7C3AED]/20">
                <div className="w-2 h-2 bg-[#7C3AED] rounded-full animate-pulse"></div>
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Functional & Integrative Medicine</span>
              </div>

              <h1 className="text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight">
                <span className="text-gray-900">Marketing for</span>
                <br />
                <span className="bg-gradient-to-r from-[#7C3AED] to-[#A855F7] bg-clip-text text-transparent">Functional Medicine</span>
                <br />
                <span className="text-gray-900">Practices</span>
              </h1>

              <p className="text-lg text-gray-600 leading-relaxed">
                Specialized digital marketing strategies for functional and integrative medicine practitioners. We help you attract patients seeking root-cause solutions, personalized care, and holistic wellness approaches through HIPAA-compliant marketing automation.
              </p>

              <div className="flex flex-wrap gap-4">
                <a
                  href="/book-strategy-call"
                  className="bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white px-8 py-4 rounded-lg hover:from-[#6D28D9] hover:to-[#9333EA] transition-all hover:shadow-lg cursor-pointer whitespace-nowrap inline-flex items-center gap-2 group font-medium"
                >
                  <span>Book Strategy Call</span>
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                </a>
                <a
                  href="/automation-platform"
                  className="border-2 border-[#7C3AED] text-[#7C3AED] px-8 py-4 rounded-lg hover:bg-[#7C3AED] hover:text-white transition-all cursor-pointer whitespace-nowrap font-medium"
                >
                  See Our Platform
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/1c4675907e02e171cafcdb05a5b26f57.jpg"
                  alt="Functional Medicine Practice"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              UNIQUE CHALLENGES
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight mb-6">
              Marketing Challenges for Functional Medicine
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Functional medicine requires educating patients about root-cause approaches and personalized care models that differ from conventional medicine.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-[#F9FAFB] p-8 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-question-line text-[#7C3AED] text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Patient Education</h3>
              <p className="text-gray-600 leading-relaxed">
                Explaining functional medicine's comprehensive approach and how it differs from conventional care requires strategic content marketing.
              </p>
            </div>

            <div className="bg-[#F9FAFB] p-8 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-time-line text-[#7C3AED] text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Longer Sales Cycle</h3>
              <p className="text-gray-600 leading-relaxed">
                Patients need time to understand the value of functional medicine, requiring nurturing campaigns and educational touchpoints.
              </p>
            </div>

            <div className="bg-[#F9FAFB] p-8 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-money-dollar-circle-line text-[#7C3AED] text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Investment Justification</h3>
              <p className="text-gray-600 leading-relaxed">
                Many services are cash-pay, requiring clear communication of value and outcomes to justify patient investment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-20 px-6 lg:px-12 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=functional%20medicine%20practitioner%20reviewing%20comprehensive%20lab%20results%20with%20patient%20personalized%20treatment%20plan%20holistic%20health%20assessment%20modern%20wellness%20clinic%20professional%20consultation&width=700&height=600&seq=func-int-solution1&orientation=landscape"
                  alt="Functional Medicine Solutions"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                  OUR SOLUTIONS
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight mb-6">
                  Specialized Marketing for Functional Medicine
                </h2>
              </div>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-article-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Educational Content Strategy</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Create comprehensive content that explains functional medicine principles, treatment approaches, and patient success stories to build trust and authority.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-robot-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Automated Lead Nurturing</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Multi-touch email sequences that educate prospects about functional medicine benefits and guide them through the decision-making process.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-search-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Condition-Specific SEO</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Target patients searching for root-cause solutions to chronic conditions like autoimmune disorders, hormonal imbalances, and digestive issues.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-star-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Patient Success Showcasing</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Highlight transformation stories and clinical outcomes to demonstrate the effectiveness of functional medicine approaches.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              PROVEN RESULTS
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
              Real Growth for Functional Medicine Practices
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-[#F9FAFB] rounded-xl">
              <div className="text-5xl font-bold bg-gradient-to-r from-[#7C3AED] to-[#A855F7] bg-clip-text text-transparent mb-3">
                +240%
              </div>
              <div className="text-gray-900 font-bold mb-2">New Patient Inquiries</div>
              <div className="text-sm text-gray-600">Within 6 months of campaign launch</div>
            </div>

            <div className="text-center p-8 bg-[#F9FAFB] rounded-xl">
              <div className="text-5xl font-bold bg-gradient-to-r from-[#7C3AED] to-[#A855F7] bg-clip-text text-transparent mb-3">
                85%
              </div>
              <div className="text-gray-900 font-bold mb-2">Lead Nurture Rate</div>
              <div className="text-sm text-gray-600">Automated email engagement</div>
            </div>

            <div className="text-center p-8 bg-[#F9FAFB] rounded-xl">
              <div className="text-5xl font-bold bg-gradient-to-r from-[#7C3AED] to-[#A855F7] bg-clip-text text-transparent mb-3">
                3.2x
              </div>
              <div className="text-gray-900 font-bold mb-2">ROI on Marketing</div>
              <div className="text-sm text-gray-600">Average across all clients</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-12 bg-gradient-to-br from-[#0D9488] to-[#14B8A6]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            Ready to Grow Your Functional Medicine Practice?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Let's discuss how our specialized marketing strategies can help you attract more patients seeking root-cause solutions.
          </p>
          <a
            href="/book-strategy-call"
            className="bg-white text-[#0D9488] px-10 py-5 rounded-lg hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-2 group font-bold text-lg shadow-xl"
          >
            <span>Schedule Your Free Strategy Call</span>
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
          </a>
        </div>
      </section>

      <RelatedServices currentSlug="functional-integrative" />
      <RelatedIndustries currentSlug="functional-integrative" />
      <Footer />
    </div>
  );
}
